import React, { useEffect } from 'react';
import RSelect from 'react-select';
import styled, { css } from 'styled-components';
import { usePrevious } from 'react-use';

const SelectWrapper = styled.div<{ label?: string; disabled?: boolean }>`
  ${({ label, disabled }) => css`
    position: relative;
    ${
      label &&
      css`
        margin-top: 4px;
      `
    }
    ${
      disabled &&
      css`
        cursor: not-allowed;
      `
    }
  `}
`;

const Label = styled.label<{ disabled?: boolean }>`
  ${({ theme, disabled }) => css`
    font-size: 1rem;
    margin: 0 0 0 4px;
    color: ${theme.colors.black};
    ${
      disabled &&
      css`
        color: ${theme.colors.grey};
      `
    }
  `}
`;

const Error = styled.span`
  color: ${(p) => p.theme.colors.danger};
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const customStyles: any = {
  control: (provided: any, state: any) => {
    const focusedColor = state.isFocused && '#FF6F61';
    return {
      ...provided,
      minHeight: 48,
      borderRadius: 8,
      outline: 'none',
      borderColor: focusedColor || '#E8E9EA',
      ':hover': {
        borderColor: focusedColor || '#A4A6AA',
      },
      boxShadow: 'none',
    };
  },
  container: (provided: any) => ({
    ...provided,
    outline: 'none',
  }),
  indicatorSeparator: () => {},
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 1100,
    // The menu portals into <body>. While a Radix modal dialog is open, Radix
    // sets pointer-events: none on <body>, which the portal would inherit —
    // making every option unclickable. Opt the portal back in explicitly.
    pointerEvents: 'auto',
  }),
};

interface SelectProps {
  onChange?: (value: any) => void;
  value?: any;
  name?: string;
  label?: string;
  error?: any;
  options?: Array<{ value: any; label: any }>;
  disabled?: boolean;
  isPortaling?: boolean;
  [key: string]: any;
}

export default function Select({
  onChange = () => {},
  value: initialValue = null,
  name = '',
  label = '',
  error = false,
  options = [],
  disabled,
  isPortaling = true,
  ...rest
}: SelectProps) {
  const initialSelectedOption = options
    ? options.filter((option) => option.value === initialValue)
    : null;
  const prevValue = usePrevious(initialValue);
  const [selected, setSelected] = React.useState(initialSelectedOption);

  const handleChange = (selectedValue: any) => {
    const { value } = selectedValue || {};
    setSelected(selectedValue);
    if (rest.isMulti) {
      onChange(selectedValue);
    } else {
      onChange(value);
    }
    if (rest.handleChangeInStatus) {
      rest.handleChangeInStatus(value);
    }
  };

  useEffect(() => {
    if (initialValue !== prevValue) {
      const next = options.find((o) => o.value === initialValue) ?? null;
      setSelected(next as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, prevValue]);

  return (
    <>
      {label && (
        <Label htmlFor={name} disabled={disabled}>
          {label}
        </Label>
      )}
      <SelectWrapper label={label} disabled={disabled}>
        <RSelect
          options={options}
          onChange={handleChange}
          value={selected}
          styles={customStyles}
          isDisabled={disabled}
          menuPortalTarget={
            isPortaling ? document.querySelector('body') : undefined
          }
          {...rest}
        />
      </SelectWrapper>

      {error && <Error>{error}</Error>}
    </>
  );
}
